import { CLOSE_CODES, type ClientMessage, type ServerMessage, type SessionResponse } from "@numbet/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { useConsent } from "../consent.ts";
import { eventsToLines, type LogLine } from "./log.ts";

export type StateMessage = Extract<ServerMessage, { type: "state" }>;
export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "invalid" | "notFound" | "left" | "replaced";

export interface RoomConnection {
  status: ConnectionStatus;
  state: StateMessage | null;
  log: LogLine[];
  /** Letzter Fehler vom Server (verschwindet nach ein paar Sekunden). */
  error: string | null;
  /** Differenz Serverzeit − lokale Zeit in ms, für Countdowns. */
  clockOffset: number;
  send(message: ClientMessage): void;
  reconnect(): void;
}

const MAX_LOG = 60;

export function useRoom(session: SessionResponse | null): RoomConnection {
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [state, setState] = useState<StateMessage | null>(null);
  const [log, setLog] = useState<LogLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const socketRef = useRef<WebSocket | null>(null);
  // Ändert sich die Einwilligung, verbindet der Client neu, damit der Server sie kennt.
  const statistik = useConsent() === "granted";

  useEffect(() => {
    if (!session) return;
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let retries = 0;

    const open = () => {
      const protocol = location.protocol === "https:" ? "wss" : "ws";
      const url = `${protocol}://${location.host}/ws?code=${session.code}&token=${encodeURIComponent(session.token)}${statistik ? "&statistik=1" : ""}`;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        retries = 0;
        setStatus("open");
        pingTimer = setInterval(() => socket.send(JSON.stringify({ type: "ping" })), 25_000);
      };
      socket.onmessage = (event) => {
        const message = JSON.parse(String(event.data)) as ServerMessage;
        if (message.type === "state") {
          setClockOffset(message.serverTime - Date.now());
          setState(message);
          const lines = eventsToLines(message.events, message.view);
          if (message.events.some((e) => e.type === "gameStarted")) setLog(lines);
          else if (lines.length > 0) setLog((old) => [...old, ...lines].slice(-MAX_LOG));
        } else if (message.type === "error") {
          setError(message.code);
        } else {
          setClockOffset(message.serverTime - Date.now());
        }
      };
      socket.onclose = (event) => {
        clearInterval(pingTimer);
        if (disposed) return;
        if (event.code === CLOSE_CODES.invalidSession) return setStatus("invalid");
        if (event.code === CLOSE_CODES.roomNotFound) return setStatus("notFound");
        if (event.code === CLOSE_CODES.left) return setStatus("left");
        if (event.code === CLOSE_CODES.replaced) return setStatus("replaced");
        setStatus("reconnecting");
        const delay = Math.min(10_000, 500 * 2 ** retries++);
        retryTimer = setTimeout(open, delay);
      };
    };

    // Nach dem Aufwachen eines Handys sofort neu verbinden statt auf den Backoff zu warten
    const onVisible = () => {
      if (document.visibilityState === "visible" && socketRef.current?.readyState === WebSocket.CLOSED && !disposed) {
        clearTimeout(retryTimer);
        retries = 0;
        open();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    setStatus("connecting");
    open();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearInterval(pingTimer);
      document.removeEventListener("visibilitychange", onVisible);
      socketRef.current?.close();
    };
  }, [session, attempt, statistik]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(timer);
  }, [error]);

  const send = useCallback((message: ClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
    else setError("network");
  }, []);

  const reconnect = useCallback(() => setAttempt((n) => n + 1), []);

  return { status, state, log, error, clockOffset, send, reconnect };
}

/** Aktuelle Serverzeit, alle `intervalMs` neu berechnet – für Countdowns. */
export function useServerNow(clockOffset: number, intervalMs = 250): number {
  const [now, setNow] = useState(() => Date.now() + clockOffset);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now() + clockOffset), intervalMs);
    setNow(Date.now() + clockOffset);
    return () => clearInterval(timer);
  }, [clockOffset, intervalMs]);
  return now;
}
